import React, { useState } from 'react';
import PreviewModal from '~/src/components/Modal/PreviewModal';

interface PreviewResponse {
  preview: {
    preview_type: 'iframe' | 'decrease_confirmation';
    preview_url?: string;
    message?: string;
  };
}

interface CourtFormsPreviewModalProps {
  previewData: PreviewResponse | null;
  currentLibraries: number;
  newTotal: number;
  isProcessing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export const CourtFormsPreviewModal = ({
  previewData,
  currentLibraries,
  newTotal,
  isProcessing,
  onCancel,
  onConfirm,
}: CourtFormsPreviewModalProps) => {
  const [isIframeLoading, setIsIframeLoading] = useState(true);

  if (!previewData?.preview) return null;

  const handleIframeLoad = () => {
    setIsIframeLoading(false);
  };

  const {
    preview_type,
    preview_url,
    message: previewMessage,
  } = previewData.preview;
  const isIncreasing = newTotal > currentLibraries;
  const quantityDiff = Math.abs(newTotal - currentLibraries);
  const formText = quantityDiff === 1 ? 'form' : 'forms';

  // Conditional parameters based on preview type
  const title = isIncreasing ? 'Increase Court Forms' : 'Decrease Court Forms';
  const confirmText = isIncreasing ? 'Confirm Update' : 'Confirm Decrease';
  const changeDescription = isIncreasing
    ? `(Increasing by ${quantityDiff} ${formText})`
    : `(Decreasing by ${quantityDiff} ${formText})`;

  const importantNotes = isIncreasing
    ? [
        'Changes to your license count will be reflected on your Clio subscription and billed with immediate effect.',
        'Please review the billing details above before confirming your change.',
      ]
    : previewMessage
    ? [previewMessage]
    : [];

  return (
    <PreviewModal
      title={title}
      onCancel={onCancel}
      onConfirm={onConfirm}
      isProcessing={isProcessing}
      confirmText={confirmText}
      cancelText="Cancel"
      beforeAfterState={{
        currentLabel: 'Current Forms',
        newLabel: 'New Forms',
        currentValue: currentLibraries.toString(),
        newValue: newTotal.toString(),
        changeDescription,
      }}
      importantNotes={importantNotes}
    >
      {preview_type === 'iframe' && preview_url && (
        <div
          className="relative"
          style={{ height: '60vh', minHeight: '400px' }}
        >
          {isIframeLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-sm text-gray-600">Loading preview...</p>
              </div>
            </div>
          )}
          <iframe
            src={preview_url}
            className="w-full h-full border border-gray-300 rounded-lg"
            title="Court Forms Preview"
            onLoad={handleIframeLoad}
          />
        </div>
      )}
    </PreviewModal>
  );
};
